import { http, HttpResponse } from 'msw';
import { mockUsers, getUserById } from './users.mock';
import type { AdminUser } from '../models/user.model';
import { USER_ENDPOINTS } from '../constants/endpoints.constants';

export const usersHandlers = [
  // Get users list
  http.get(USER_ENDPOINTS.USERS, async ({ request }) => {
    const url = new URL(request.url);
    const page = parseInt(url.searchParams.get('page') || '1');
    const pageSize = parseInt(url.searchParams.get('pageSize') || '10');
    const search = url.searchParams.get('search');

    let filtered = [...mockUsers];

    if (search) {
      const term = search.toLowerCase();
      filtered = filtered.filter(
        u =>
          u.FirstName.toLowerCase().includes(term) ||
          u.LastName.toLowerCase().includes(term) ||
          u.Email.toLowerCase().includes(term)
      );
    }

    const total = filtered.length;

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300));

    return HttpResponse.json({
      status: true,
      status_code: 200,
      message: 'Success',
      data: {
        count: total,
        users: filtered,
      },
      pagination: {
        page,
        pageSize,
        total,
        totalPages: Math.ceil(total / pageSize),
      },
    });
  }),

  // Get single user by id
  http.get(USER_ENDPOINTS.USER_BY_ID(':id'), ({ params }) => {
    const { id } = params;
    let user = getUserById(id as string);

    if (!user && id && typeof id === 'string' && id.length > 10) {
      user = {
        UserId: id,
        FirstName: 'Demo',
        LastName: 'User',
        Email: 'demo.user@example.com',
        Role: 'NORMAL_USER',
        Status: true,
      };
    }

    if (!user) {
      return HttpResponse.json({ 
        status: false,
        status_code: 404,
        message: 'User not found',
        data: null,
        error: 'Not Found' 
      }, { status: 404 });
    }

    return HttpResponse.json({
      status: true,
      status_code: 200,
      message: 'OK',
      data: {
        user: {
          id: user.UserId,
          email: user.Email,
          full_name: `${user.FirstName} ${user.LastName}`.trim(),
          role: user.Role.toLowerCase(),
          is_active: user.Status,
        },
      },
      error: null,
    });
  }),

  // Create user
  http.post(USER_ENDPOINTS.CREATE_USER, async ({ request }) => {
    const payload = (await request.json()) as Partial<AdminUser> & {
      firstName?: string;
      lastName?: string;
      email?: string;
      role?: AdminUser['Role'];
    };

    const newUser: AdminUser = {
      UserId: `u-${Math.floor(Math.random() * 10000)}`,
      FirstName: payload.firstName || 'New',
      LastName: payload.lastName || 'User',
      Email: payload.email || 'new.user@example.com',
      Role: (payload.role as AdminUser['Role']) || 'NORMAL_USER',
      Status: true,
    };

    mockUsers.push(newUser);

    return HttpResponse.json({
        status: true,
        status_code: 201,
        message: 'Created',
        data: {
            user: {
                id: newUser.UserId,
                email: newUser.Email,
                full_name: `${newUser.FirstName} ${newUser.LastName}`.trim(),
                role: newUser.Role.toLowerCase(),
                is_active: newUser.Status,
            }
        },
        error: null
    }, { status: 201 });
  }),

  // Update user
  http.patch(USER_ENDPOINTS.UPDATE_USER(':id'), async ({ params, request }) => {
    const { id } = params as { id: string };
    const raw = (await request.json()) as Record<string, unknown>;

    const idx = mockUsers.findIndex(u => u.UserId === id);
    if (idx === -1) {
      return HttpResponse.json({ error: 'User not found' }, { status: 404 });
    }

    const prev = mockUsers[idx];
    let firstName = prev.FirstName;
    let lastName = prev.LastName;
    if (typeof raw.full_name === 'string') {
      const parts = raw.full_name.trim().split(/\s+/).filter(Boolean);
      firstName = parts[0] ?? firstName;
      lastName = parts.slice(1).join(' ') || lastName;
    }
    const email = typeof raw.email === 'string' ? raw.email : prev.Email;
    let role = prev.Role;
    if (raw.role === 'super_admin') role = 'SUPER_ADMIN';
    else if (raw.role === 'admin') role = 'NORMAL_ADMIN';
    const status =
      typeof raw.is_active === 'boolean' ? raw.is_active : prev.Status;

    const legacy = raw as Partial<AdminUser>;
    const updated: AdminUser = {
      ...prev,
      FirstName: legacy.FirstName ?? firstName,
      LastName: legacy.LastName ?? lastName,
      Email: legacy.Email ?? email,
      Role: (legacy.Role as AdminUser['Role']) ?? role,
      Status: legacy.Status ?? status,
    };
    mockUsers[idx] = updated;

    return HttpResponse.json({
      status: true,
      status_code: 200,
      message: 'Updated',
      data: {
        user: {
          id: updated.UserId,
          email: updated.Email,
          full_name: `${updated.FirstName} ${updated.LastName}`.trim(),
          role:
            updated.Role === 'SUPER_ADMIN'
              ? 'super_admin'
              : updated.Role === 'NORMAL_ADMIN'
                ? 'admin'
                : String(updated.Role).toLowerCase(),
          is_active: updated.Status,
        },
      },
      error: null,
    });
  }),

  // Delete user
  http.delete(USER_ENDPOINTS.DELETE_USER(':id'), ({ params }) => {
    const { id } = params as { id: string };
    const idx = mockUsers.findIndex(u => u.UserId === id);
    if (idx === -1) {
      return HttpResponse.json({ error: 'User not found' }, { status: 404 });
    }

    mockUsers.splice(idx, 1);
    return HttpResponse.json(null, { status: 204 });
  }),
];
